import { Link } from "react-router-dom";
import Reveal from "../components/Reveal";
import SegmentedSwitch from "../components/SegmentedSwitch";
import SectionLabel from "../components/SectionLabel";
import useDocumentTitle from "../hooks/useDocumentTitle";
import { OBJECT_TYPES, STYLE_SERIES, PACKAGES } from "../data/content";

export default function Konzepte() {
  useDocumentTitle(
    "Konzepte & Objekttypen",
    "Ein Konzept beginnt mit dem Objekt. Ferienhaus, Ferienwohnung, Apartment oder Refit — Livinora plant passgenaue Einrichtungskonzepte für jeden Objekttyp."
  );

  return (
    <div data-testid="page-konzepte">
      {/* Hero */}
      <section className="pt-28 sm:pt-32 md:pt-44 pb-16 md:pb-24">
        <div className="container-livinora grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8">
            <Reveal>
              <SectionLabel index="∞" text="Konzepte" />
            </Reveal>
            <Reveal delay={60}>
              <h1 className="display-xl mt-8 text-[44px] sm:text-[60px] md:text-[80px] lg:text-[96px] text-balance">
                Ein Konzept beginnt
                <br className="hidden sm:block" />
                mit dem Objekt.
              </h1>
            </Reveal>
            <Reveal delay={140}>
              <p className="prose-livinora prose-livinora--lead mt-8 max-w-2xl">
                Vier Objekttypen, ein System: Stilserie, Paket und Anfrage greifen ineinander —
                vom Ferienhaus bis zum Refit-Bestand.
              </p>
            </Reveal>
          </div>
          <div className="md:col-span-4 flex md:justify-end">
            <SegmentedSwitch testId="konzepte-audience" />
          </div>
        </div>
      </section>

      {OBJECT_TYPES.map((o, i) => (
        <section
          key={o.slug}
          id={o.slug}
          className={`py-20 md:py-28 ${
            i % 2 === 0 ? "bg-[color:var(--livinora-paper)]" : "bg-[color:var(--livinora-paper-warm)]"
          } border-t border-[color:var(--livinora-hairline)]`}
          data-testid={`konzept-${o.slug}`}
        >
          <div className="container-livinora grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className={`lg:col-span-6 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
              <Reveal variant="mask">
                <div className="media-wrap aspect-[5/6]">
                  <img src={o.image} alt={o.name} className="w-full h-full object-cover" loading="lazy" />
                </div>
              </Reveal>
            </div>
            <div className={`lg:col-span-6 ${i % 2 === 1 ? "lg:order-1" : ""}`}>
              <Reveal>
                <SectionLabel index={`0${i + 1}`} text="Konzeptweg" />
              </Reveal>
              <Reveal delay={60}>
                <h2 className="display-xl text-[40px] sm:text-[52px] md:text-[64px] mt-7 text-balance">
                  {o.name}
                </h2>
              </Reveal>
              <Reveal delay={140}>
                <p className="mt-7 serif italic text-xl sm:text-2xl text-[color:var(--livinora-ink-soft)] leading-snug">
                  {o.pitch}
                </p>
              </Reveal>
              <Reveal delay={220}>
                <p className="prose-livinora mt-6 max-w-xl">{o.description}</p>
              </Reveal>
              <Reveal delay={300}>
                <ul className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm">
                  {o.highlights.map((h) => (
                    <li key={h} className="flex items-center gap-3">
                      <span className="w-3 h-px bg-[color:var(--livinora-ink)]" /> {h}
                    </li>
                  ))}
                </ul>
              </Reveal>

              <Reveal delay={360}>
                <div className="mt-10 flex flex-wrap gap-8">
                  <div>
                    <div className="micro-label">Passende Stilserien</div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {STYLE_SERIES.filter((s) =>
                        s.suitedFor.some((t) => t.toLowerCase() === o.name.toLowerCase())
                      ).map((s) => (
                        <Link
                          key={s.slug}
                          to={`/stilserien#${s.slug}`}
                          className="text-[11px] uppercase tracking-[0.2em] px-3 py-2 border border-[color:var(--livinora-hairline)] hover:border-[color:var(--livinora-ink)] transition-colors"
                        >
                          {s.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="micro-label">Verfügbare Pakete</div>
                    <div className="mt-3 flex gap-2">
                      {PACKAGES.map((p) => (
                        <span
                          key={p.key}
                          className="text-[11px] uppercase tracking-[0.2em] px-3 py-2 border border-[color:var(--livinora-hairline)]"
                        >
                          {p.name}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </Reveal>

              <Reveal delay={420}>
                <div className="mt-12 flex flex-wrap gap-4">
                  <Link
                    to={`/konfigurator?object=${o.slug}`}
                    className="btn-livinora btn-primary btn-magnetic"
                    data-testid={`konzept-${o.slug}-cta-konfigurator`}
                  >
                    Projekt konfigurieren <span>→</span>
                  </Link>
                  <Link
                    to={`/shop${o.slug === "ferienhaus" || o.slug === "ferienwohnung" || o.slug === "apartment" ? "" : "?room=refit"}`}
                    className="btn-livinora btn-ghost"
                    data-testid={`konzept-${o.slug}-cta-shop`}
                  >
                    Raumsets ansehen
                  </Link>
                </div>
              </Reveal>
            </div>
          </div>
        </section>
      ))}
    </div>
  );
}
